import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { setMultiPostError } from 'services/compose'
import { BLUESKY_PHOTO_SIZE_ERROR_CODE } from 'services/compose/multi'
import { BulkContentPost } from 'services/compose/state'
import { BLUESKY_PHOTO_SIZE_LIMIT, BRAND_BLUESKY } from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import { getFileSize } from 'utils/file/helpers'

const SIZE_ERROR = { code: BLUESKY_PHOTO_SIZE_ERROR_CODE, message: 'Your image exceeds the 1MB size limit for Bluesky.' }

/** Flags/clears the Bluesky photo size error for a bulk post, fetching the size for url-only images. */
export function useBlueskyImageSizeError(post: BulkContentPost, isVideo: boolean) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const { network, imageUrl, imageUpload, id } = post

  const postErrorRef = useRef(post.error)
  postErrorRef.current = post.error

  useEffect(() => {
    // Every exit has to clear a previously flagged error - the post can leave the
    // Bluesky+image path (video attached, image removed) while the error is still on it.
    const clearSizeError = () => {
      if (postErrorRef.current?.code === BLUESKY_PHOTO_SIZE_ERROR_CODE) {
        dispatch(setMultiPostError({ postId: id, error: undefined }))
      }
    }

    if (network !== BRAND_BLUESKY || isVideo) {
      clearSizeError()
      return undefined
    }

    if (imageUpload?.file) {
      const size = imageUpload.file.size
      if (size > BLUESKY_PHOTO_SIZE_LIMIT) {
        if (postErrorRef.current?.code !== BLUESKY_PHOTO_SIZE_ERROR_CODE) {
          dispatch(setMultiPostError({ postId: id, error: SIZE_ERROR }))
        }
      } else {
        clearSizeError()
      }
      return undefined
    }

    if (imageUrl) {
      // The size check is async — ignore results that arrive after unmount or after the image changed.
      let cancelled = false
      getFileSize(imageUrl).then(size => {
        if (cancelled) {
          return
        }
        if (size !== null && size !== undefined && size > BLUESKY_PHOTO_SIZE_LIMIT) {
          if (postErrorRef.current?.code !== BLUESKY_PHOTO_SIZE_ERROR_CODE) {
            dispatch(setMultiPostError({ postId: id, error: SIZE_ERROR }))
          }
        } else {
          clearSizeError()
        }
      })
      return () => {
        cancelled = true
      }
    }

    // No image left on the post.
    clearSizeError()
    return undefined
  }, [network, imageUrl, imageUpload, id, dispatch, isVideo])
}
